import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format, isMatch, isValid, parse } from 'date-fns';
import FileUploadStep from '../ImportWizard/FileUploadStep';
import WizardProgress from '../ImportWizard/WizardProgress';
import Button from '../../UI/Button';
import Checkbox from '../../UI/Checkbox';
import Select from '../../UI/Select';
import Input from '../../UI/Input';
import Loader from '../../UI/Loader';
import ErrorMessages from '../../UI/ErrorMessages';
import ApiErrorMessages from '../../UI/ApiErrorMessages';
import { CSVColumn } from '../ImportWizard';
import Program from '../../../models/serviceModels/Program';
import Client from '../../../models/serviceModels/Client';
import { createProgram, searchPrograms } from '../../../services/programsService';
import { searchClients } from '../../../services/clientsService';
import { importProgramEnrollmentRow, previewProgramEnrollmentImport, ProgramEnrollmentImportRow } from '../../../services/programEnrollmentImportService';
import { formatOptionalJSONDateWithoutTime } from '../../../utils/dateHelpers';
import { useNavigate } from 'react-router-dom';

const REQUIRED_COLUMNS = ['program_name', 'participant_first_name', 'participant_last_name', 'participant_dob', 'participant_phone_number', 'start_date', 'end_date'];
const REQUIRED_VALUES = ['program_name', 'participant_first_name', 'participant_last_name', 'start_date'];

type SourceRow = Record<string, string>;
type ProgramMappings = Record<string, string>;
type ClientMappings = Record<number, Client>;
type RowError = { rowIndex: number; field: string; message: string };
type ImportExecutionResult = { success: number; failed: RowError[]; skipped: RowError[] };

interface ProgramEnrollmentImportSession {
    step: number;
    rows: SourceRow[];
    programs: Program[];
    programMappings: ProgramMappings;
    createPrograms: Record<string, boolean>;
    clientMappings: ClientMappings;
    errors: RowError[];
    progress: number;
    result?: ImportExecutionResult;
}

const SESSION_STORAGE_KEY = 'program-enrollment-import-session-v1';

const normalize = (value: string | undefined | null) => String(value || '').trim().toLowerCase();
const dateValue = (value: string) => {
    if (!value || (!isMatch(value, 'MM/dd/yyyy') && !isMatch(value, 'MM-dd-yyyy') && !isMatch(value, 'yyyy-MM-dd'))) return undefined;
    const pattern = value.includes('/') ? 'MM/dd/yyyy' : value.includes('-') && value.length === 10 && value.startsWith('20') ? 'yyyy-MM-dd' : 'MM-dd-yyyy';
    const parsed = parse(value, pattern, new Date());
    return isValid(parsed) ? parsed : undefined;
};
const toApiDate = (value: string) => {
    const parsed = dateValue(value);
    return parsed ? `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}` : value;
};
const formatImportDate = (value: string) => {
    const parsed = dateValue(value);
    return parsed ? format(parsed, 'MM/dd/yyyy') : value;
};
const normalizePhone = (value: string) => value.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
const mappingAnchorId = (type: 'program' | 'participant', value: string | number) => `${type}-mapping-${encodeURIComponent(String(value))}`;

interface ProgramEnrollmentImportProps {
    onBackToImportTypes?: () => void;
}

const ProgramEnrollmentImport: React.FC<ProgramEnrollmentImportProps> = ({ onBackToImportTypes }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [rows, setRows] = useState<SourceRow[]>([]);
    const [programs, setPrograms] = useState<Program[]>([]);
    const [programMappings, setProgramMappings] = useState<ProgramMappings>({});
    const [createPrograms, setCreatePrograms] = useState<Record<string, boolean>>({});
    const [clientMappings, setClientMappings] = useState<ClientMappings>({});
    const [errors, setErrors] = useState<RowError[]>([]);
    const [apiError, setApiError] = useState<any>();
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentRecord, setCurrentRecord] = useState(0);
    const [result, setResult] = useState<ImportExecutionResult>();
    const [sessionRestored, setSessionRestored] = useState(false);
    const [needsValidation, setNeedsValidation] = useState(false);
    const [mappingFocus, setMappingFocus] = useState<{ type: 'program' | 'participant'; value: string | number }>();
    const importStartedRef = useRef(false);

    useEffect(() => {
        if (step !== 5 || result || loading || importStartedRef.current) return;
        importStartedRef.current = true;
        execute();
    }, [step, result, loading]);

    useEffect(() => {
        if (!mappingFocus || (mappingFocus.type === 'program' && step !== 2) || (mappingFocus.type === 'participant' && step !== 3)) return;
        const timer = setTimeout(() => {
            document.getElementById(mappingAnchorId(mappingFocus.type, mappingFocus.value))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setMappingFocus(undefined);
        }, 0);
        return () => clearTimeout(timer);
    }, [mappingFocus, step]);

    useEffect(() => {
        try {
            const serialized = localStorage.getItem(SESSION_STORAGE_KEY);
            if (serialized) {
                const saved = JSON.parse(serialized) as ProgramEnrollmentImportSession;
                if (saved.rows?.length) {
                    setStep(saved.step || 1);
                    setRows(saved.rows);
                    setPrograms(saved.programs || []);
                    setProgramMappings(saved.programMappings || {});
                    setCreatePrograms(saved.createPrograms || {});
                    setClientMappings(saved.clientMappings || {});
                    setErrors(saved.errors || []);
                    setProgress(saved.progress || 0);
                    setResult(saved.result);
                }
            }
        } catch (error) {
            console.warn('Unable to restore program enrollment import session', error);
            localStorage.removeItem(SESSION_STORAGE_KEY);
        } finally {
            setSessionRestored(true);
        }
    }, []);

    useEffect(() => {
        if (!sessionRestored || rows.length === 0) return;
        const session: ProgramEnrollmentImportSession = { step, rows, programs, programMappings, createPrograms, clientMappings, errors, progress, result };
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    }, [sessionRestored, step, rows, programs, programMappings, createPrograms, clientMappings, errors, progress, result]);

    const sourcePrograms = useMemo(() => Array.from(new Set(rows.map(row => row.program_name).filter(Boolean))).sort((a, b) => a.localeCompare(b)), [rows]);
    const upload = async (_file: File, data: any[], columns: CSVColumn[]) => {
        const normalizedColumns = columns.map(column => normalize(column.name));
        const missing = REQUIRED_COLUMNS.filter(column => !normalizedColumns.includes(column));
        if (missing.length) {
            setErrors(missing.map(column => ({ rowIndex: -1, field: column, message: `Missing required column: ${column}` })));
            return;
        }
        const canonicalRows = data.map(row => REQUIRED_COLUMNS.reduce((result, column) => {
            const key = Object.keys(row).find(candidate => normalize(candidate) === column);
            result[column] = key ? String(row[key] || '').trim() : '';
            return result;
        }, {} as SourceRow));
        const activePrograms = await searchPrograms({ query: '', skip: 0, take: 1000, includeDeactivated: false });
        const initialMappings = canonicalRows.reduce((mapped, row) => {
            const match = activePrograms.data.find(program => normalize(program.name) === normalize(row.program_name));
            if (match) mapped[row.program_name] = match.id;
            return mapped;
        }, {} as ProgramMappings);
        setRows(canonicalRows);
        setPrograms(activePrograms.data);
        setProgramMappings(initialMappings);
        setCreatePrograms({});
        setErrors([]);
        setStep(2);
    };

    const createProgramsForFinalImport = async (validRowIndexes: Set<number>): Promise<ProgramMappings> => {
        const resolvedMappings = { ...programMappings };
        const createdPrograms: Program[] = [];
        for (const sourceName of sourcePrograms) {
            if (!createPrograms[sourceName]) continue;
            if (!rows.some((row, index) => row.program_name === sourceName && validRowIndexes.has(index))) continue;
            const created = await createProgram({ name: sourceName });
            createdPrograms.push(created);
            resolvedMappings[sourceName] = created.id;
        }
        if (createdPrograms.length) {
            setPrograms(current => [...current, ...createdPrograms].sort((a, b) => a.name.localeCompare(b.name)));
            setProgramMappings(resolvedMappings);
            setCreatePrograms({});
        }
        return resolvedMappings;
    };

    const loadClientMatches = async () => {
        setLoading(true);
        const resolved: ClientMappings = {};
        try {
            await Promise.all(rows.map(async (row, rowIndex) => {
                if (clientMappings[rowIndex]) return;
                if (!row.participant_dob || !row.participant_phone_number) return;
                const response = await searchClients({
                    query: `${row.participant_first_name} ${row.participant_last_name} ${row.participant_dob} ${row.participant_phone_number}`,
                    skip: 0, take: 10, includeDeactivated: true
                });
                const matches = response.data.filter(client => normalize(client.firstName) === normalize(row.participant_first_name)
                    && normalize(client.lastName) === normalize(row.participant_last_name)
                    && String(client.dateOfBirth || '').slice(0, 10) === toApiDate(row.participant_dob)
                    && normalizePhone(client.phoneNumber || '') === normalizePhone(row.participant_phone_number));
                if (matches.length === 1) resolved[rowIndex] = matches[0];
            }));
            setClientMappings(current => ({ ...current, ...resolved }));
            setStep(3);
        } catch (error) { setApiError(error); } finally { setLoading(false); }
    };

    const localValidation = (rowsToValidate = rows): RowError[] => {
        const found: RowError[] = [];
        rowsToValidate.forEach((row, rowIndex) => {
            REQUIRED_VALUES.forEach(field => { if (!row[field]) found.push({ rowIndex, field, message: `${field.replace(/_/g, ' ')} is required` }); });
            if (!programMappings[row.program_name] && !createPrograms[row.program_name]) found.push({ rowIndex, field: 'program_name', message: 'A program must be mapped or created' });
            if (!clientMappings[rowIndex]) found.push({ rowIndex, field: 'participant', message: 'A participant must be mapped' });
            const start = dateValue(row.start_date); const end = row.end_date ? dateValue(row.end_date) : undefined;
            if (!start) found.push({ rowIndex, field: 'start_date', message: 'Start date must be MM/DD/YYYY, MM-DD-YYYY, or YYYY-MM-DD' });
            if (row.end_date && !end) found.push({ rowIndex, field: 'end_date', message: 'End date must be MM/DD/YYYY, MM-DD-YYYY, or YYYY-MM-DD' });
            if (start && end && start > end) found.push({ rowIndex, field: 'end_date', message: 'End date must be on or after start date' });
        });
        for (let firstIndex = 0; firstIndex < rowsToValidate.length; firstIndex++) {
            for (let secondIndex = firstIndex + 1; secondIndex < rowsToValidate.length; secondIndex++) {
                const first = rowsToValidate[firstIndex];
                const second = rowsToValidate[secondIndex];
                const firstProgram = programMappings[first.program_name] || `new:${normalize(first.program_name)}`;
                const secondProgram = programMappings[second.program_name] || `new:${normalize(second.program_name)}`;
                if (clientMappings[firstIndex]?.id !== clientMappings[secondIndex]?.id || firstProgram !== secondProgram) continue;
                const firstStart = dateValue(first.start_date)?.getTime();
                const firstEnd = first.end_date ? (dateValue(first.end_date)?.getTime() ?? Infinity) : Infinity;
                const secondStart = dateValue(second.start_date)?.getTime();
                const secondEnd = second.end_date ? (dateValue(second.end_date)?.getTime() ?? Infinity) : Infinity;
                if (firstStart !== undefined && secondStart !== undefined && firstStart < secondEnd && secondStart < firstEnd) {
                    found.push({ rowIndex: firstIndex, field: 'dates', message: `This enrollment overlaps row ${secondIndex + 1}` });
                    found.push({ rowIndex: secondIndex, field: 'dates', message: `This enrollment overlaps row ${firstIndex + 1}` });
                }
            }
        }
        return found;
    };

    const preview = async (rowsToValidate = rows) => {
        const localErrors = localValidation(rowsToValidate);
        setLoading(true); setApiError(undefined);
        try {
            const locallyInvalidRows = new Set(localErrors.map(error => error.rowIndex));
            const previewRows = getApiRows(programMappings, rowsToValidate).filter(row => !!row.clientId && !!row.programId && !locallyInvalidRows.has(row.rowIndex));
            if (previewRows.length === 0) {
                setErrors(localErrors);
                setNeedsValidation(false);
                setStep(4);
                return localErrors;
            }
            const remoteErrors = await previewProgramEnrollmentImport(previewRows);
            const validationErrors = [...localErrors, ...remoteErrors];
            setErrors(validationErrors);
            setNeedsValidation(false);
            setStep(4);
            return validationErrors;
        } catch (error) { setApiError(error); return localErrors; } finally { setLoading(false); }
    };

    const getApiRows = (mappings = programMappings, sourceRows = rows): ProgramEnrollmentImportRow[] => sourceRows.map((row, rowIndex) => ({
        rowIndex,
        clientId: clientMappings[rowIndex]?.id || '',
        programId: mappings[row.program_name],
        startDate: toApiDate(row.start_date),
        endDate: row.end_date ? toApiDate(row.end_date) : undefined
    }));

    const execute = async () => {
        setLoading(true); setApiError(undefined);
        setProgress(0); setCurrentRecord(0);
        const failed: RowError[] = []; let success = 0;
        const skipped = errors.filter(error => error.rowIndex >= 0);
        const invalidRowIndexes = new Set(skipped.map(error => error.rowIndex));
        let apiRows: ProgramEnrollmentImportRow[];
        try {
            const validRowIndexes = new Set(rows.map((_row, index) => index).filter(index => !invalidRowIndexes.has(index)));
            apiRows = getApiRows(await createProgramsForFinalImport(validRowIndexes)).filter(row => validRowIndexes.has(row.rowIndex));
        } catch (error: any) {
            setApiError(error);
            setLoading(false);
            return;
        }
        for (let index = 0; index < apiRows.length; index++) {
            setCurrentRecord(index + 1);
            try { await importProgramEnrollmentRow(apiRows[index]); success++; }
            catch (error: any) { failed.push({ rowIndex: apiRows[index].rowIndex, field: 'import', message: error?.response?.data?.error || 'Unable to import enrollment' }); }
            setProgress(Math.round(((index + 1) / apiRows.length) * 100));
        }
        setResult({ success, failed, skipped }); setLoading(false);
    };

    const downloadImportErrorReport = () => {
        if (!result?.failed.length) return;
        const csv = [
            ['Row', 'Error'].join(','),
            ...result.failed.map(error => [error.rowIndex + 1, `"${error.message.replace(/"/g, '""')}"`].join(','))
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'program_enrollment_import_errors.csv';
        link.click();
        window.URL.revokeObjectURL(url);
    };

    const importMoreData = () => {
        localStorage.removeItem(SESSION_STORAGE_KEY);
        navigate('/import');
    };

    const RowErrors = ({ rowIndex }: { rowIndex: number }) => <ErrorMessages messages={errors.filter(error => error.rowIndex === rowIndex).map(error => error.message)} />;

    return <div className="section"><div className="row justify-content-center"><div className="column">
        <h1 className="form-header">Program Enrollment Import</h1>
        {step === 1 && onBackToImportTypes && <div className="wizard-import-type-back"><Button id="back-to-import-types" className="secondary" text="Choose a Different Import Type" onClick={onBackToImportTypes} /></div>}
        <WizardProgress className="program-enrollment-wizard-progress" currentStep={step} steps={[{ number: 1, label: 'File Upload' }, { number: 2, label: 'Program Mapping' }, { number: 3, label: 'Participant Mapping' }, { number: 4, label: 'Validation Preview' }, { number: 5, label: 'Import Summary' }]} />
        <div className="program-enrollment-import-errors"><ApiErrorMessages error={apiError} />
        {errors.filter(error => error.rowIndex === -1).length > 0 && <ErrorMessages messages={errors.filter(error => error.rowIndex === -1).map(error => error.message)} />}</div>
        {step === 1 && <FileUploadStep title="Upload Program Enrollment File" description={<>Upload a CSV or tab-delimited file with these columns: <code>{REQUIRED_COLUMNS.join(', ')}</code>.</>} onNext={upload} />}
        {step === 2 && <div className="wizard-content program-enrollment-mapping-step"><h2 className="section-header">Map Programs</h2><p>Programs were matched by name where possible. Select an existing program or create one for each source name.</p>{loading && <Loader />}
            <div className="card margin-vertical-4">
                <div className="card-header"><strong>Program Mapping - {sourcePrograms.length} unique program value{sourcePrograms.length === 1 ? '' : 's'} shown from {rows.length} enrollment row{rows.length === 1 ? '' : 's'}</strong></div>
                <div className="table-container table-mapping">
                    <table className="table">
                        <thead><tr><th>Imported Program Name</th><th>Maps To</th><th>Create New Program</th></tr></thead>
                        <tbody>{sourcePrograms.map(name => <tr id={mappingAnchorId('program', name)} key={name}>
                            <td><strong>{name}</strong></td>
                            <td><Select id={`program-${name}`} value={programMappings[name] || ''} disabled={!!createPrograms[name]} onChange={id => setProgramMappings(current => ({ ...current, [name]: id }))}><option value="">Select a program</option>{programs.map(program => <option key={program.id} value={program.id}>{program.name}</option>)}</Select></td>
                            <td><Checkbox id={`create-program-${name}`} label={`Create “${name}”`} checked={!!createPrograms[name]} onChange={checked => { setCreatePrograms(current => ({ ...current, [name]: checked })); if (checked) setProgramMappings(current => { const next = { ...current }; delete next[name]; return next; }); }} /></td>
                        </tr>)}</tbody>
                    </table>
                </div>
            </div>
            <div className="row justify-content-between margin-top-5"><div className="column-auto"><Button id="back-to-enrollment-upload" className="secondary" text="Back" onClick={() => setStep(1)} /></div><div className="column-auto"><Button id="next-to-participant-mapping" text="Next" onClick={loadClientMatches} disabled={loading || sourcePrograms.some(name => !createPrograms[name] && !programMappings[name])} /></div></div></div>}
        {step === 3 && <ParticipantMapping rows={rows} mappings={clientMappings} onChange={(index, client) => setClientMappings(current => {
            const next = { ...current };
            if (client) next[index] = client;
            else delete next[index];
            return next;
        })} onBack={() => { setErrors([]); setApiError(undefined); setStep(2); }} onNext={() => { void preview(); }} errors={errors} loading={loading} />}
        {step === 4 && <EnrollmentValidationPreview rows={rows} programs={programs} programMappings={programMappings} createPrograms={createPrograms} clientMappings={clientMappings} errors={errors} needsValidation={needsValidation} loading={loading} onRowChange={(index, field, value) => { setRows(current => current.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row)); setNeedsValidation(true); }} onBack={() => { setErrors([]); setApiError(undefined); setStep(3); }} onEditProgramMapping={programName => { setErrors([]); setApiError(undefined); setMappingFocus({ type: 'program', value: programName }); setNeedsValidation(true); setStep(2); }} onEditParticipantMapping={index => { setErrors([]); setApiError(undefined); setMappingFocus({ type: 'participant', value: index }); setNeedsValidation(true); setStep(3); }} onValidate={preview} onContinue={() => { importStartedRef.current = false; setStep(5); }} />}
        {step === 5 && !result && <div className="wizard-content program-enrollment-execution-step"><h2 className="section-header">Import & Results</h2><div className="card margin-vertical-4"><div className="card-header"><strong>Import Execution</strong></div><div className="card-body"><div className="progress-container"><div className="row align-items-center margin-bottom-3"><div className="column"><strong>Importing Program Enrollments...</strong></div><div className="column-auto"><strong>{progress}%</strong></div></div><div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }} /></div><div className="row align-items-center margin-top-3"><div className="column-auto"><Loader noMarginTop={true} /></div><div className="column-auto"><span>Processing enrollment {currentRecord} of {rows.length - new Set(errors.filter(error => error.rowIndex >= 0).map(error => error.rowIndex)).size}...</span></div></div></div></div></div></div>}
        {step === 5 && result && <ImportEnrollmentSummary totalRows={rows.length} result={result} onDownloadErrors={downloadImportErrorReport} onImportMore={importMoreData} />}
    </div></div></div>;
};

const ParticipantMapping: React.FC<{ rows: SourceRow[]; mappings: ClientMappings; onChange: (index: number, client?: Client) => void; onBack: () => void; onNext: () => void; errors: RowError[]; loading: boolean }> = ({ rows, mappings, onChange, onBack, onNext, errors, loading }) => {
    const [queries, setQueries] = useState<Record<number, string>>({});
    const [results, setResults] = useState<Record<number, Client[]>>({});
    const [searching, setSearching] = useState<number>();
    const [autoMatching, setAutoMatching] = useState(false);
    const attemptedAutoMatches = useRef(new Set<number>());
    const participantGroups = useMemo(() => {
        const groups = new Map<string, { row: SourceRow; indexes: number[] }>();
        rows.forEach((row, index) => {
            const key = [row.participant_first_name, row.participant_last_name, row.participant_dob, normalizePhone(row.participant_phone_number || '')].map(normalize).join('|');
            const group = groups.get(key);
            if (group) group.indexes.push(index);
            else groups.set(key, { row, indexes: [index] });
        });
        return Array.from(groups.values());
    }, [rows]);

    const applyGroupMapping = (indexes: number[], client?: Client) => {
        indexes.forEach(index => onChange(index, client));
    };

    useEffect(() => {
        const closeOnOutsideClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.participant-mapping-search-results')) {
                setResults({});
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setResults({});
            }
        };

        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, []);

    useEffect(() => {
        const autoMatchParticipants = async () => {
            const rowsToMatch = rows.map((row, index) => ({ row, index }))
                .filter(({ row, index }) => !mappings[index] && !!row.participant_dob && !!row.participant_phone_number && !attemptedAutoMatches.current.has(index));
            if (rowsToMatch.length === 0) return;

            rowsToMatch.forEach(({ index }) => attemptedAutoMatches.current.add(index));
            setAutoMatching(true);
            try {
                await Promise.all(rowsToMatch.map(async ({ row, index }) => {
                    const response = await searchClients({
                        query: `${row.participant_first_name} ${row.participant_last_name} ${row.participant_dob} ${row.participant_phone_number}`,
                        skip: 0,
                        take: 10,
                        includeDeactivated: true
                    });
                    const exactMatches = response.data.filter(client => normalize(client.firstName) === normalize(row.participant_first_name)
                        && normalize(client.lastName) === normalize(row.participant_last_name)
                        && String(client.dateOfBirth || '').slice(0, 10) === toApiDate(row.participant_dob)
                        && normalizePhone(client.phoneNumber || '') === normalizePhone(row.participant_phone_number));
                    if (exactMatches.length === 1) onChange(index, exactMatches[0]);
                    if (exactMatches.length === 1) setQueries(current => ({ ...current, [index]: '' }));
                }));
            } finally {
                setAutoMatching(false);
            }
        };

        autoMatchParticipants().catch(error => console.error('Unable to automatically match import participants', error));
    }, [rows, mappings, onChange]);

    const search = async (index: number, indexes: number[]) => {
        setSearching(index);
        try {
            const response = await searchClients({ query: queries[index] || '', skip: 0, take: 100, includeDeactivated: true });
            if (response.data.length === 1) {
                applyGroupMapping(indexes, response.data[0]);
                setResults(current => ({ ...current, [index]: [] }));
                setQueries(current => ({ ...current, [index]: '' }));
                return;
            }
            setResults(current => ({ ...current, [index]: response.data }));
        } finally {
            setSearching(undefined);
        }
    };
    return <div className="wizard-content program-enrollment-participant-mapping-step"><h2 className="section-header">Map Participants</h2><p>Participants with complete, unique name, date-of-birth, and phone matches are mapped automatically. Deactivated participants are labelled clearly.</p>{autoMatching && <p className="text-muted">Finding matching participants…</p>}
        <div className="card margin-vertical-4">
            <div className="card-header"><strong>Participant Mapping - {participantGroups.length} unique participant value{participantGroups.length === 1 ? '' : 's'} shown from {rows.length} enrollment row{rows.length === 1 ? '' : 's'}</strong></div>
            <div className="table-container table-mapping">
                <table className="table program-enrollment-participant-mapping-table">
                    <thead><tr><th>Row</th><th>Imported Participant</th><th>Date of Birth</th><th>Phone Number</th><th>Maps To</th></tr></thead>
                    <tbody>{participantGroups.map(({ row, indexes }) => { const index = indexes[0]; const groupErrors = errors.filter(error => indexes.includes(error.rowIndex)); return <React.Fragment key={index}>
                        <tr id={mappingAnchorId('participant', index)} data-participant-mapping-indexes={indexes.join(' ')} className={!mappings[index] ? 'participant-mapping-unmapped' : ''}>
                            <td>{indexes.map(rowIndex => rowIndex + 1).join(', ')}</td>
                            <td><strong>{row.participant_first_name} {row.participant_last_name}</strong></td>
                            <td>{row.participant_dob || <span className="text-muted">Not provided</span>}</td>
                            <td>{row.participant_phone_number || <span className="text-muted">Not provided</span>}</td>
                            <td><div className="participant-mapping-search">
                                {mappings[index] ? <div className="participant-mapping-selected"><a href={`/participants/${mappings[index].id}/profile-demographics`} target="_blank" rel="noreferrer"><strong>{mappings[index].firstName} {mappings[index].lastName}</strong></a>{mappings[index].deactivated && <span className="badge margin-start-2">Deactivated</span>}<a className="program-enrollment-history-link" href={`/participants/${mappings[index].id}/programs`} target="_blank" rel="noreferrer">View program enrollments</a></div> : <div className="participant-mapping-unmapped-status">Not mapped</div>}
                                <div className="row align-items-end margin-top-2"><div className="column"><Input id={`participant-search-${index}`} value={queries[index] || ''} onChange={value => setQueries(current => ({ ...current, [index]: value }))} /></div><div className="column-auto"><Button id={`search-participant-${index}`} className="secondary small" text="Search" loading={searching === index} onClick={() => search(index, indexes)} /></div></div>
                                {results[index]?.length > 0 && <div className="participant-mapping-search-results"><div className="row justify-content-end margin-bottom-2"><div className="column-auto"><Button id={`close-participant-results-${index}`} className="secondary small" text="Close results" onClick={() => setResults(current => ({ ...current, [index]: [] }))} /></div></div><table><thead><tr><th>Participant</th><th>Date of Birth</th><th>Phone</th><th>Status</th><th></th></tr></thead><tbody>{results[index].map(client => <tr key={client.id}><td><a href={`/participants/${client.id}/profile-demographics`} target="_blank" rel="noreferrer">{client.firstName} {client.lastName}</a></td><td>{client.dateOfBirth ? formatOptionalJSONDateWithoutTime(client.dateOfBirth, 'MM/dd/yyyy') : '—'}</td><td>{client.phoneNumber || '—'}</td><td>{client.deactivated ? <span className="badge">Deactivated</span> : 'Active'}</td><td><Button id={`select-client-${index}-${client.id}`} className="secondary small" text="Select" onClick={() => { applyGroupMapping(indexes, client); setResults(current => ({ ...current, [index]: [] })); setQueries(current => ({ ...current, [index]: '' })); }} /></td></tr>)}</tbody></table></div>}
                            </div></td>
                        </tr>
                        {groupErrors.length > 0 && <tr className="program-enrollment-participant-mapping-errors"><td colSpan={5}><ErrorMessages messages={groupErrors.map(error => `Row ${error.rowIndex + 1}: ${error.message}`)} /></td></tr>}
                    </React.Fragment>; })}</tbody>
                </table>
            </div>
        </div>
        <div className="row justify-content-between margin-top-5"><div className="column-auto"><Button id="back-to-program-mapping" className="secondary" text="Back" onClick={onBack} /></div><div className="column-auto"><Button id="validate-program-enrollments" text="Validate Enrollments" loading={loading} disabled={loading || rows.some((_row, index) => !mappings[index])} onClick={onNext} /></div></div>
    </div>;
};

const EnrollmentValidationPreview: React.FC<{
    rows: SourceRow[];
    programs: Program[];
    programMappings: ProgramMappings;
    createPrograms: Record<string, boolean>;
    clientMappings: ClientMappings;
    errors: RowError[];
    needsValidation: boolean;
    loading: boolean;
    onRowChange: (index: number, field: string, value: string) => void;
    onBack: () => void;
    onEditProgramMapping: (programName: string) => void;
    onEditParticipantMapping: (index: number) => void;
    onValidate: (rowsToValidate?: SourceRow[]) => Promise<RowError[]>;
    onContinue: () => void;
}> = ({ rows, programs, programMappings, createPrograms, clientMappings, errors, needsValidation, loading, onRowChange, onBack, onEditProgramMapping, onEditParticipantMapping, onValidate, onContinue }) => {
    const [showInvalidOnly, setShowInvalidOnly] = useState(errors.length > 0);
    const [editingRow, setEditingRow] = useState<number | null>(null);
    const [editedDates, setEditedDates] = useState({ startDate: '', endDate: '' });
    const invalidRows = new Set(errors.filter(error => error.rowIndex >= 0).map(error => error.rowIndex));
    const visibleRows = rows.map((row, index) => ({ row, index })).filter(({ index }) => showInvalidOnly ? invalidRows.has(index) : !invalidRows.has(index));
    const beginEditing = (index: number, row: SourceRow) => {
        setEditingRow(index);
        setEditedDates({ startDate: formatImportDate(row.start_date), endDate: row.end_date ? formatImportDate(row.end_date) : '' });
    };
    const saveDates = async (index: number) => {
        const updatedRows = rows.map((row, rowIndex) => rowIndex === index ? {
            ...row,
            start_date: formatImportDate(editedDates.startDate),
            end_date: editedDates.endDate ? formatImportDate(editedDates.endDate) : ''
        } : row);
        onRowChange(index, 'start_date', updatedRows[index].start_date);
        onRowChange(index, 'end_date', updatedRows[index].end_date);
        setEditingRow(null);
        const validationErrors = await onValidate(updatedRows);
        if (!validationErrors.some(error => error.rowIndex === index)) {
            setShowInvalidOnly(false);
        }
    };

    useEffect(() => {
        if (invalidRows.size === 0) {
            setShowInvalidOnly(false);
        }
    }, [errors, invalidRows.size]);

    return <div className="wizard-content program-enrollment-validation-preview">
        <h2 className="section-header">Validation Preview</h2>
        <p>Review and correct enrollment data before importing. Revalidate after editing a value.</p>
        <div className="row align-items-center margin-vertical-4">
            <div className="column-auto"><div className="tabs">
                <button id="show-valid-enrollment-rows" className={`tab ${!showInvalidOnly ? 'active' : ''}`} onClick={() => setShowInvalidOnly(false)} style={{ color: '#16a34a', alignItems: 'center', display: 'flex' }}><i className="ri-checkbox-circle-line" style={{ fontSize: '24px', color: '#16a34a' }} />&nbsp;Valid Rows ({rows.length - invalidRows.size})</button>
                <button id="show-invalid-enrollment-rows" className={`tab ${showInvalidOnly ? 'active' : ''}`} onClick={() => setShowInvalidOnly(true)} style={{ color: '#dc2626', alignItems: 'center', display: 'flex' }}><i className="ri-error-warning-line" style={{ fontSize: '24px', color: '#dc2626' }} />&nbsp;Invalid Rows ({invalidRows.size})</button>
            </div></div>
            {needsValidation && <div className="column"><span className="text-danger">Changes need validation.</span></div>}
        </div>
        <div className="card margin-vertical-4">
            <div className="card-header"><strong>{showInvalidOnly ? 'Invalid Enrollment Rows' : 'Valid Enrollment Rows'}</strong></div>
            <div className="table-container table-mapping"><table className="table program-enrollment-validation-table">
                <thead><tr><th>Actions</th><th>Row</th><th>Program</th><th>Participant</th><th>Start Date</th><th>End Date</th></tr></thead>
                <tbody>{visibleRows.map(({ row, index }) => {
                    const mappedProgram = programs.find(program => program.id === programMappings[row.program_name]);
                    const mappedClient = clientMappings[index];
                    const rowErrors = errors.filter(error => error.rowIndex === index);
                    const hasExistingEnrollmentOverlap = rowErrors.some(error => error.message.includes('overlaps an existing enrollment'));
                    return <React.Fragment key={index}>
                        <tr className={rowErrors.length ? 'row-invalid' : ''}>
                            <td>{editingRow === index ? <div className="row"><div className="column-auto"><Button id={`save-validation-dates-${index}`} className="small" text="Save" onClick={() => saveDates(index)} /></div><div className="column-auto"><Button id={`cancel-validation-dates-${index}`} className="secondary small" text="Cancel" onClick={() => setEditingRow(null)} /></div></div> : <Button id={`edit-validation-dates-${index}`} className="secondary small" text="Edit" onClick={() => beginEditing(index, row)} />}</td>
                            <td>{index + 1}</td>
                            <td><strong>{row.program_name}</strong><div className="program-enrollment-mapped-value">{mappedProgram ? <>Maps To: <strong>{mappedProgram.name}</strong></> : createPrograms[row.program_name] ? <span className="program-enrollment-create-chip">Will be created</span> : <span className="text-danger">Not mapped</span>}</div><Button id={`edit-program-mapping-${index}`} className="secondary small margin-top-2" text="Edit Program Mapping" onClick={() => onEditProgramMapping(row.program_name)} /></td>
                            <td><strong>{row.participant_first_name} {row.participant_last_name}</strong><div className="program-enrollment-mapped-value">DOB: {row.participant_dob ? formatImportDate(row.participant_dob) : 'Not provided'} · Phone: {row.participant_phone_number || 'Not provided'}</div>{mappedClient ? <div className="program-enrollment-mapped-value">Maps to: <a href={`/participants/${mappedClient.id}/profile-demographics`} target="_blank" rel="noreferrer">{mappedClient.firstName} {mappedClient.lastName}</a>{mappedClient.deactivated && <span className="badge margin-start-2">Deactivated</span>}</div> : <div className="program-enrollment-mapped-value text-danger">Not mapped</div>}<Button id={`edit-participant-mapping-${index}`} className="secondary small margin-top-2" text="Edit Participant Mapping" onClick={() => onEditParticipantMapping(index)} /></td>
                            <td>{editingRow === index ? <><div className="program-enrollment-existing-value">Current: {formatImportDate(row.start_date)}</div><Input id={`validation-start-date-${index}`} value={editedDates.startDate} onChange={value => setEditedDates(current => ({ ...current, startDate: value }))} /></> : formatImportDate(row.start_date)}</td>
                            <td>{editingRow === index ? <><div className="program-enrollment-existing-value">Current: {row.end_date ? formatImportDate(row.end_date) : 'No end date'}</div><Input id={`validation-end-date-${index}`} value={editedDates.endDate} onChange={value => setEditedDates(current => ({ ...current, endDate: value }))} /></> : row.end_date ? formatImportDate(row.end_date) : '—'}</td>
                        </tr>
                        {rowErrors.length > 0 && <tr className="program-enrollment-participant-mapping-errors"><td colSpan={6}><ErrorMessages messages={rowErrors.map(error => error.message)} />{hasExistingEnrollmentOverlap && mappedClient && <div className="margin-top-2"><a className="program-enrollment-history-link" href={`/participants/${mappedClient.id}/programs`} target="_blank" rel="noreferrer">View program enrollments</a></div>}</td></tr>}
                    </React.Fragment>;
                })}</tbody>
            </table></div>
        </div>
        {visibleRows.length === 0 && <p className="text-muted">No {showInvalidOnly ? 'invalid' : 'valid'} rows to show.</p>}
        <div className="row justify-content-between margin-top-5"><div className="column-auto"><Button id="back-to-participant-mapping" className="secondary" text="Back" disabled={loading} onClick={onBack} /></div><div className="column-auto"><Button id="continue-to-program-enrollment-import" text="Begin Import" disabled={loading || needsValidation || rows.length === invalidRows.size} onClick={onContinue} /></div></div>
    </div>;
};

const ImportEnrollmentSummary: React.FC<{ totalRows: number; result: ImportExecutionResult; onDownloadErrors: () => void; onImportMore: () => void }> = ({ totalRows, result, onDownloadErrors, onImportMore }) => {
    const skippedCount = new Set(result.skipped.map(error => error.rowIndex)).size;
    return <div className="wizard-content program-enrollment-execution-step">
        <h2 className="section-header">Import & Results</h2>
        <div className="card margin-vertical-4"><div className="card-header"><strong>Import Summary</strong></div><div className="card-body">
            <div className="row margin-top-3"><div className="column-6"><div className="stat-box"><div className="stat-icon"><i className="ri-file-list-line" /></div><div className="stat-content"><div className="stat-label">Total Rows Processed:</div><div className="stat-value">{totalRows}</div></div></div></div><div className="column-6"><div className="stat-box success"><div className="stat-icon"><i className="ri-checkbox-circle-line" /></div><div className="stat-content"><div className="stat-label">Successfully Imported:</div><div className="stat-value">{result.success}</div></div></div></div></div>
            <div className="row margin-top-3"><div className="column-6"><div className="stat-box error"><div className="stat-icon"><i className="ri-error-warning-line" /></div><div className="stat-content"><div className="stat-label">Failed During Import:</div><div className="stat-value">{result.failed.length}</div></div></div></div><div className="column-6"><div className="stat-box"><div className="stat-icon"><i className="ri-skip-right-line" /></div><div className="stat-content"><div className="stat-label">Skipped Validation Rows:</div><div className="stat-value">{skippedCount}</div></div></div></div></div>
            {result.failed.length > 0 && <div className="margin-top-4"><ErrorMessages messages={result.failed.map(error => `Row ${error.rowIndex + 1}: ${error.message}`)} /></div>}
            {result.failed.length > 0 && <div className="row margin-top-4"><div className="column-6"><Button id="download-program-enrollment-error-report" text="Download Error Report (CSV)" icon={<i className="ri-download-line" />} className="secondary full-width" onClick={onDownloadErrors} /></div></div>}
        </div></div>
        <div className="row justify-content-center margin-top-5"><div className="column-auto"><Button id="import-more-program-enrollment-data" text="Import More Data" onClick={onImportMore} /></div></div>
    </div>;
};

export default ProgramEnrollmentImport;
